import React from "react";
import { TempleColor } from "@/types/temple.types";

interface TempleSvgProps {
  readonly color: TempleColor;
  readonly children: React.ReactNode;
  readonly className?: string;
  readonly svgClassName?: string;
  readonly icon?: React.ReactNode;
}

const colorMap: Record<
  TempleColor,
  { bg: string; accent: string; dark: string; light: string }
> = {
  green: {
    bg: "#E6F4EC",
    accent: "#34A868",
    dark: "#C5E3D2",
    light: "#F2FAF5",
  },
  blue: {
    bg: "#E5EFF9",
    accent: "#4B7FCA",
    dark: "#C4D8EE",
    light: "#F0F6FC",
  },
  orange: {
    bg: "#F5EDE2",
    accent: "#C3964A",
    dark: "#E4D5C2",
    light: "#FBF6F0",
  },
  gray: {
    bg: "#EDEDEC",
    accent: "#9B9B97",
    dark: "#DCDCDA",
    light: "#F6F6F5",
  },
};

export function TempleSvg({
  color,
  children,
  className = "",
  svgClassName = "",
  icon,
}: TempleSvgProps) {
  const colors = colorMap[color];

  return (
    <div className={`relative w-full max-w-md mx-auto ${className}`}>
      <div className="relative">
        <svg
          viewBox="0 0 400 500"
          className={`w-full h-auto transition-transform duration-300 ${svgClassName}`}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          {/* Base steps */}
          <rect
            x="18"
            y="457"
            width="364"
            height="30"
            fill={colors.dark}
            opacity="0.5"
          />
          <rect x="15" y="455" width="370" height="30" fill={colors.bg} />
          <rect x="15" y="455" width="370" height="5" fill={colors.light} />
          <line
            x1="15"
            y1="470"
            x2="385"
            y2="470"
            stroke={colors.dark}
            strokeWidth="1"
            opacity="0.3"
          />
          <rect
            x="15"
            y="460"
            width="10"
            height="25"
            fill={colors.dark}
            opacity="0.3"
          />
          <rect
            x="375"
            y="460"
            width="10"
            height="25"
            fill={colors.light}
            opacity="0.4"
          />

          <rect
            x="33"
            y="437"
            width="334"
            height="18"
            fill={colors.dark}
            opacity="0.4"
          />
          <rect x="30" y="435" width="340" height="20" fill={colors.bg} />
          <rect
            x="30"
            y="435"
            width="340"
            height="4"
            fill={colors.light}
            opacity="0.8"
          />
          <rect
            x="30"
            y="440"
            width="10"
            height="15"
            fill={colors.dark}
            opacity="0.25"
          />
          <rect
            x="360"
            y="440"
            width="10"
            height="15"
            fill={colors.light}
            opacity="0.35"
          />

          <rect
            x="48"
            y="422"
            width="304"
            height="13"
            fill={colors.dark}
            opacity="0.4"
          />
          <rect
            x="45"
            y="420"
            width="310"
            height="15"
            fill={colors.dark}
            opacity="0.5"
          />
          <rect
            x="45"
            y="420"
            width="310"
            height="3"
            fill={colors.bg}
            opacity="0.7"
          />
          <rect
            x="45"
            y="425"
            width="8"
            height="10"
            fill={colors.dark}
            opacity="0.5"
          />
          <rect
            x="347"
            y="425"
            width="8"
            height="10"
            fill={colors.dark}
            opacity="0.3"
          />

          {/* Main body */}
          <rect
            x="52"
            y="137"
            width="296"
            height="283"
            fill={colors.dark}
            opacity="0.15"
          />
          <rect x="50" y="135" width="300" height="285" fill={colors.bg} />
          <rect
            x="50"
            y="135"
            width="12"
            height="285"
            fill={colors.dark}
            opacity="0.25"
          />
          <rect
            x="338"
            y="135"
            width="12"
            height="285"
            fill={colors.light}
            opacity="0.6"
          />

          {/* Decorative columns */}
          <rect
            x="80"
            y="155"
            width="10"
            height="245"
            fill={colors.dark}
            opacity="0.1"
            rx="2"
          />
          <rect
            x="310"
            y="155"
            width="10"
            height="245"
            fill={colors.light}
            opacity="0.4"
            rx="2"
          />

          {/* Decorative bands */}
          <rect
            x="50"
            y="180"
            width="300"
            height="6"
            fill={colors.dark}
            opacity="0.15"
          />
          <rect
            x="50"
            y="260"
            width="300"
            height="4"
            fill={colors.dark}
            opacity="0.1"
          />
          <rect
            x="50"
            y="370"
            width="300"
            height="6"
            fill={colors.dark}
            opacity="0.15"
          />

          {/* Roof */}
          <path
            d="M 200 38 L 388 138 L 12 138 Z"
            fill={colors.dark}
            opacity="0.4"
          />
          <path d="M 200 30 L 385 135 L 15 135 Z" fill={colors.bg} />
          <path
            d="M 200 30 L 15 135 L 35 135 L 200 45 Z"
            fill={colors.dark}
            opacity="0.2"
          />
          <path
            d="M 200 30 L 385 135 L 365 135 L 200 45 Z"
            fill={colors.light}
            opacity="0.5"
          />
          <path
            d="M 200 30 L 280 85 L 120 85 Z"
            fill={colors.light}
            opacity="0.7"
          />

          <line
            x1="200"
            y1="30"
            x2="385"
            y2="135"
            stroke={colors.dark}
            strokeWidth="1.5"
            opacity="0.2"
          />
          <line
            x1="200"
            y1="30"
            x2="15"
            y2="135"
            stroke={colors.dark}
            strokeWidth="1.5"
            opacity="0.2"
          />

          <rect
            x="15"
            y="135"
            width="370"
            height="6"
            fill={colors.dark}
            opacity="0.2"
          />
          <rect
            x="15"
            y="135"
            width="370"
            height="1.5"
            fill={colors.light}
            opacity="0.4"
          />

          {/* Accent line */}
          <rect
            x="40"
            y="133"
            width="320"
            height="1.5"
            fill={colors.accent}
            opacity="0.3"
          />
        </svg>
      </div>

      {/* Icon */}
      {icon && (
        <div className="absolute top-[23%] left-1/2 -translate-x-1/2 z-20">
          {icon}
        </div>
      )}

      {/* Content container */}
      <div className="absolute top-[32%] left-[15%] right-[15%] z-10">
        <div className="bg-white rounded-xl py-4 shadow-[0_2px_8px_rgba(0,0,0,0.06)] border border-black/[0.04]">
          {children}
        </div>
      </div>
    </div>
  );
}
